import { useEffect } from 'react'
import { clearCurrentTsgId, revokeAllTsgTokens } from '../../framework/tsg'
import { showLoadingMask, clearUserSessionForLogout } from '../mainOneappNew/fawkesUtils'
import { getFrameworkVar } from '../../framework/states'
import { logout } from '../../framework/actions'
import { inactivityLogout } from '../mainOneappNew/InactivityTimeout'
import fetch from '../../libs/fetch'
import { __error, __info } from '../../init/debug'
import type { StoreState } from '../../framework/stateTypes'

const iframeLoad = (url: string, retryCb?: (iframe: HTMLIFrameElement) => Promise<void> | void) => new Promise((resolve, reject) => {
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  const remove = () => document.body.removeChild(iframe)
  const complete = () => {
    remove()
    resolve(undefined)
  }
  iframe.onload = complete
  let retried = false
  iframe.onerror = async () => {
    const msg = 'silent sign out iframe error'
    __error(msg)
    if (retryCb && !retried) {
      try {
        retried = true
        await retryCb(iframe)
        if (iframe.src === url) {
          complete()
        } // else wait for iframe.onload or onerror again
      }
      catch (e) {
        remove()
        reject(e)
      }
    }
    else {
      remove()
      reject(new Error(msg))
    }
  }
  iframe.onabort = () => {
    __info('silent sign out iframe aborted')
    complete()
  }
  iframe.setAttribute('sandbox', '') // disable js redirection
  iframe.src = url
  document.body.appendChild(iframe)
})

const SPARKY_LOGOUT_CHANNEL = 'sparky-logout'
const logoutChannel = new BroadcastChannel(SPARKY_LOGOUT_CHANNEL) as BroadcastChannel & { closed?: boolean, user_id?: string }
const logoutHandler = (event: MessageEvent) => {
  __info('received login/logout message from channel', event.data)
  const close = () => {
    logoutChannel.close()
    logoutChannel.closed = true
  }
  switch (event.data) {
    case 'silent':
      close()
      inactivityLogout('')
      break
    case 'logout':
      close()
      logout()
      break
    default:
      if (event.data?.login && event.data.user_id === logoutChannel.user_id) {
        return // ignore login message for current user
      }
      close()
      // TODO: logout tokens only
  }
}
logoutChannel.addEventListener('message', logoutHandler)
const sendLogoutMessage = (message: string = 'logout') => {
  if (!logoutChannel.closed) {
    logoutChannel.removeEventListener('message', logoutHandler)
    logoutChannel.postMessage(message)
    logoutChannel.close()
    logoutChannel.closed = true
  }
}

export const sendLoginMessage = (user_id: string) => {
  if (!logoutChannel.closed) {
    logoutChannel.user_id = user_id
    logoutChannel.postMessage({
      login: true,
      user_id,
    })
  }
}

export const useLogout = (state: StoreState) => useEffect(() => {
  if (state.loggingOut === 'silent') {
    const userManager = state.oidc?.userManager
    if (userManager) {
      if ('__logging_out' in userManager) {
        return // already called
      }
      clearCurrentTsgId()
      const silentSignout = async () => {
        Object.defineProperty(userManager, '__logging_out', { value: true })
        const id_token_hint = await userManager.getUser().then(user => user?.id_token, () => undefined)
        if (!id_token_hint) {
          throw new Error('not able to get oidc id_token')
        }
        const endpoint = await userManager.metadataService.getEndSessionEndpoint()
        if (!endpoint) {
          throw new Error('not able to get oidc end session endpoint')
        }
        const oidcClientCfg = getFrameworkVar('oidc_client')!
        const {
          client_id = oidcClientCfg.client_id as string,
          post_logout_redirect_uri = oidcClientCfg.post_logout_redirect_uri as string,
        } = userManager.settings
        await revokeAllTsgTokens()
        await userManager.revokeTokens()
        userManager.removeUser() // will call clearUserSessionForLogout
        sendLogoutMessage('silent')
        const url = new URL(endpoint)
        url.searchParams.set('id_token_hint', id_token_hint)
        url.searchParams.set('client_id', client_id)
        const endSessionFetchUrl = url.toString()
        url.searchParams.set('post_logout_redirect_uri', post_logout_redirect_uri)
        const endSessionIframeUrl = url.toString()
        await iframeLoad(endSessionIframeUrl, async (iframe) => { // retry with fetch
          await fetch(endSessionFetchUrl, { method: 'GET' }).catch(() => { })
          iframe.src = post_logout_redirect_uri // will trigger onload or onerror again
        })
      }
      silentSignout().catch((err) => {
        __error('failed to silent sign out, redirect to sign out instead', err)
        sendLogoutMessage('silent')
        userManager.signoutRedirect()
      })
    }
    else { // force saml logout
      __error('no oidc user manager avaialble, hub saml signout instead')
      clearUserSessionForLogout()
      sendLogoutMessage()
      const postRedirectUrl = getFrameworkVar('oidc_client')?.post_logout_redirect_uri
      if (postRedirectUrl) {
        iframeLoad(postRedirectUrl).catch((err) => {
          __error('failed to silent sign out for hub, redirect to hub sign out instead', err)
          window.location.href = postRedirectUrl
        })
      }
    }
  }
  else if (state.loggingOut) {
    showLoadingMask()
    if (state.oidc) {
      clearCurrentTsgId()
      revokeAllTsgTokens().then(() => {
        sendLogoutMessage()
      }).then(state.oidc.logout)
    }
    else {
      clearUserSessionForLogout()
      sendLogoutMessage()
      const postRedirectUrl = getFrameworkVar('oidc_client')?.post_logout_redirect_uri
      const { location } = window
      if (postRedirectUrl) { // force saml logout
        location.href = postRedirectUrl
        return
      }
      if (location.pathname.startsWith('/authentication/')) {
        location.replace('/')
      }
      else {
        location.reload()
      }
    }
  }
}, [state.loggingOut, state.oidc])

export default useLogout
